import request from "../axios";
import {
  buildFeedbackPayload,
  buildHistoryQuery,
  buildQualityQuery,
  FeedbackDimension,
  FeedbackErrorType,
  HistoryDays,
  QualityFeedbackPayload,
  QualityQuery,
  QualityQueue,
  StandardIssueType,
} from "../utils/mobileQuality";

export interface MobileQualityDashboard {
  date: string;
  capturedCount: number;
  evaluatedCount: number;
  qualifiedCount: number;
  unqualifiedCount: number;
  captureIssueCount: number;
  pendingReviewCount: number;
  qualifiedRate: number;
}

export interface MobileQualityRecord {
  id: string;
  capturedAt: string;
  storeId: string;
  storeName?: string;
  dishId?: string;
  dishName?: string;
  captureImageUrl?: string;
  qualityResult?: "QUALIFIED" | "UNQUALIFIED" | "AI_REVIEW_REQUIRED";
  dishMatchResult?: "SAME" | "DIFFERENT" | "UNCERTAIN";
  qualityDimensions?: Record<string, string>;
  failedDimensions?: string[];
  captureStatus: "VALID" | "INVALID_CAPTURE";
  aiReason?: string;
  temperatureCelsius?: number;
  aiJudgeFeedback?: 0 | 1 | null;
  aiJudgeFeedbackNote?: string;
  feedbackErrorType?: FeedbackErrorType | "UNSPECIFIED";
  correctedDishId?: string;
  correctedDishName?: string;
  dishMissing?: boolean;
  expectedQualityResult?: "QUALIFIED" | "UNQUALIFIED" | "AI_REVIEW_REQUIRED";
  feedbackDisputedDimensions?: FeedbackDimension[];
  feedbackActualProblemDimensions?: FeedbackDimension[];
  feedbackStandardIssueTypes?: StandardIssueType[];
  aiJudgeFeedbackAt?: string;
  standardImageUrl?: string;
}

export interface MobileQualityHistory {
  days: HistoryDays;
  startDate: string;
  endDate: string;
  capturedCount: number;
  evaluatedCount: number;
  qualifiedCount: number;
  unqualifiedCount: number;
  captureIssueCount: number;
  pendingReviewCount: number;
  qualifiedRate: number;
  previousEvaluatedCount: number;
  previousQualifiedRate: number;
  qualifiedRateChange: number;
  items: MobileQualityDashboard[];
}

export interface PageResult<T> {
  records: T[];
  total: number;
  current: number;
  size: number;
  pages: number;
}

export interface FeedbackDishOption {
  id: string;
  name: string;
}

export async function getQualityDashboard(params: {
  date: string;
  storeId?: string | number;
  dishId?: string | number;
}): Promise<MobileQualityDashboard> {
  const { data } = await request.get("/mobile/quality/dashboard", params);
  return data;
}

export async function getQualityHistory(params: {
  days: HistoryDays;
  storeId?: string | number;
  dishId?: string | number;
}): Promise<MobileQualityHistory> {
  const { data } = await request.get("/mobile/quality/history/days", buildHistoryQuery(params));
  return data;
}

export async function getQualityRecords(query: QualityQuery): Promise<PageResult<MobileQualityRecord>> {
  const { data } = await request.get("/mobile/quality/records", buildQualityQuery(query));
  return data;
}

export async function getQualityRecord(recordId: string): Promise<MobileQualityRecord> {
  const { data } = await request.get(`/mobile/quality/records/${recordId}`);
  return data;
}

export async function getFeedbackDishOptions(): Promise<FeedbackDishOption[]> {
  const { data } = await request.get("/dish/list/all", { name: null });
  const values = data?.dishList || data?.records || (Array.isArray(data) ? data : []);
  return values
    .map((item: any) => ({ id: String(item.id), name: item.name || item.dishName || "" }))
    .filter((item: FeedbackDishOption) => item.id && item.name);
}

export async function submitQualityFeedback(recordId: string, payload: QualityFeedbackPayload) {
  const { data } = await request.post(
    `/mobile/quality/records/${recordId}/feedback`,
    buildFeedbackPayload(payload)
  );
  return data as MobileQualityRecord;
}

export type { QualityQueue };
